import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  Home,
  Map as MapIcon,
  TrendingUp,
  FileText,
  Package,
  Users,
  Building2,
  Search,
} from 'lucide-react';
import { agentLabApi, personasApiV2, type PersonaClient, type PersonaDetail, type SkillTile } from '@/services/api';
import { useCreativeWorkbench } from './CreativeWorkbenchContext';

interface Command {
  id: string;
  title: string;
  keywords?: string[];
  icon: React.ElementType;
  group: 'Navigate' | 'Client' | 'Persona' | 'Skill';
  run: () => void;
}

const NAV_COMMANDS: Omit<Command, 'run'>[] = [
  { id: 'nav-home', title: 'Go to Home', icon: Home, group: 'Navigate' },
  { id: 'nav-strategy', title: 'Go to Strategy', icon: MapIcon, group: 'Navigate' },
  { id: 'nav-performance', title: 'Go to Performance', icon: TrendingUp, group: 'Navigate' },
  { id: 'nav-briefs', title: 'Go to Briefs', icon: FileText, group: 'Navigate' },
  { id: 'nav-assets', title: 'Go to Assets', icon: Package, group: 'Navigate' },
];

const NAV_TARGETS: Record<string, string> = {
  'nav-home': '/creative',
  'nav-strategy': '/creative/strategy',
  'nav-performance': '/creative/performance',
  'nav-briefs': '/creative/briefs',
  'nav-assets': '/creative/assets',
};

export function CommandPalette() {
  const navigate = useNavigate();
  const location = useLocation();
  const { clientUid, setClient, setPersona } = useCreativeWorkbench();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [clients, setClients] = useState<PersonaClient[]>([]);
  const [personas, setPersonas] = useState<PersonaDetail[]>([]);
  /** Flattened skill commands from smart-tiles (fetched when palette opens) */
  const [skillCommands, setSkillCommands] = useState<Command[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const isInWorkbench = location.pathname.startsWith('/creative');

  // Load clients once.
  useEffect(() => {
    let cancelled = false;
    personasApiV2
      .listClients()
      .then((r) => {
        if (!cancelled) setClients(r.clients || []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Load personas when the workbench's client changes.
  useEffect(() => {
    if (!clientUid) {
      setPersonas([]);
      return;
    }
    let cancelled = false;
    personasApiV2
      .listPersonas(clientUid)
      .then((r) => {
        if (!cancelled) setPersonas(r.personas || []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [clientUid]);

  // ⌘K / Ctrl+K toggle — active only inside /creative/*.
  useEffect(() => {
    if (!isInWorkbench) return;
    function handler(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    }
    function openHandler() {
      setOpen(true);
    }
    window.addEventListener('keydown', handler);
    window.addEventListener('creative-workbench:open-palette', openHandler);
    return () => {
      window.removeEventListener('keydown', handler);
      window.removeEventListener('creative-workbench:open-palette', openHandler);
    };
  }, [isInWorkbench, open]);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
      setQuery('');
    }
  }, [open]);

  // Fetch smart skill tiles when palette opens (with a client context).
  // Flatten CategoryTile sub-skills into "Category › Sub-skill" commands.
  useEffect(() => {
    if (!open || !clientUid) {
      setSkillCommands([]);
      return;
    }
    let cancelled = false;
    agentLabApi
      .getSmartTiles(clientUid, undefined, 5, 'explore')
      .then((res) => {
        if (cancelled) return;
        const cmds: Command[] = [];
        for (const tile of res.tiles ?? []) {
          if (tile.tile_type === 'category') {
            // Flatten sub-skills into "Category › Sub-skill" searchable commands
            for (const sub of tile.sub_skills) {
              const params = new URLSearchParams({
                skill: sub.skill_id,
                prompt: sub.trigger_prompt,
                ...(clientUid ? { client: clientUid } : {}),
                signal: sub.signal_label,
              });
              cmds.push({
                id: `skill-cat-${tile.skill_id}-${sub.skill_id}`,
                title: `${tile.skill_name} › ${sub.skill_name}`,
                keywords: [sub.skill_id, sub.signal_label, tile.category],
                icon: TrendingUp,
                group: 'Skill',
                run: () => navigate(`/chat/new?${params.toString()}`),
              });
            }
          } else {
            const directTile = tile as SkillTile;
            const params = new URLSearchParams({
              skill: directTile.skill_id,
              prompt: directTile.trigger_prompt,
              ...(clientUid ? { client: clientUid } : {}),
            });
            cmds.push({
              id: `skill-${directTile.skill_id}`,
              title: directTile.skill_name,
              keywords: [directTile.skill_id, directTile.category, directTile.pitch],
              icon: Search,
              group: 'Skill',
              run: () => navigate(`/chat/new?${params.toString()}`),
            });
          }
        }
        setSkillCommands(cmds);
      })
      .catch(() => setSkillCommands([]));
    return () => { cancelled = true; };
  }, [open, clientUid, navigate]);

  const commands = useMemo<Command[]>(() => {
    const list: Command[] = NAV_COMMANDS.map((c) => ({
      ...c,
      run: () => navigate(NAV_TARGETS[c.id]),
    }));
    for (const client of clients) {
      list.push({
        id: `client-${client.client_uid}`,
        title: `Switch client to ${client.client_name || client.client_uid}`,
        keywords: [client.client_uid, client.client_name ?? ''],
        icon: Building2,
        group: 'Client',
        run: () => setClient(client.client_uid),
      });
    }
    for (const persona of personas) {
      const id = persona.id || persona.slug || '';
      if (!id) continue;
      list.push({
        id: `persona-${id}`,
        title: `Switch persona to ${persona.name ?? id}`,
        keywords: [id, persona.name ?? ''],
        icon: Users,
        group: 'Persona',
        run: () => setPersona(id),
      });
    }
    // Skill commands are pre-built in a separate effect (fetched on open)
    list.push(...skillCommands);
    return list;
  }, [clients, personas, skillCommands, navigate, setClient, setPersona]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands;
    return commands.filter((c) => {
      const haystack = [c.title, ...(c.keywords ?? [])].join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [commands, query]);

  const grouped = useMemo<Array<[Command['group'], Command[]]>>(() => {
    const map = new Map<Command['group'], Command[]>();
    for (const cmd of filtered) {
      const arr = map.get(cmd.group) ?? [];
      arr.push(cmd);
      map.set(cmd.group, arr);
    }
    return Array.from(map.entries());
  }, [filtered]);

  if (!open || !isInWorkbench) return null;

  const runCommand = (cmd: Command) => {
    cmd.run();
    setOpen(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-24 bg-black/30 backdrop-blur-sm"
      onClick={() => setOpen(false)}
      role="dialog"
      aria-label="Command palette"
    >
      <div
        className="w-full max-w-xl bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
          <Search size={16} className="text-slate-400" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search commands, clients, personas..."
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-slate-400"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && filtered[0]) {
                runCommand(filtered[0]);
              }
            }}
          />
          <kbd className="text-[10px] font-mono text-slate-400 border border-slate-200 rounded px-1.5 py-0.5">
            esc
          </kbd>
        </div>
        <div className="max-h-80 overflow-y-auto py-2">
          {filtered.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-slate-400">
              No commands match
            </div>
          ) : (
            grouped.map(([group, items]) => (
              <div key={group} className="mb-2">
                <div className="px-4 py-1 text-[10px] font-mono uppercase tracking-wider text-slate-400">
                  {group}
                </div>
                {items.map((cmd) => (
                  <button
                    key={cmd.id}
                    onClick={() => runCommand(cmd)}
                    className={clsx(
                      'w-full flex items-center gap-3 px-4 py-2 text-sm text-left hover:bg-slate-50 transition-colors',
                    )}
                  >
                    <cmd.icon size={14} className="text-slate-400" />
                    <span className="flex-1 truncate">{cmd.title}</span>
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
